import type {EventSaveErrorProps, EventWizardReduxState} from '@Components/event-wizard/event-wizard.types';
import {EVENT_ERROR_LOG_TAG} from '@Components/event-wizard/event-wizard.types';
import {EventCreationErrorType, EventWizardProgressStep, EventWizardSourceType, LOADING_TOAST_KEY, MESSAGE_GROWL_KEY, RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {EventRegistrationFields} from '@Components/events/events.types';
import type {TicketingConfig} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {buildTicketingConfigFromEventVO, getEffectiveQuestionSectionTitle, getEffectiveRegisterBtnText, getEventTypeGa4Param, getTicketingSetupCompleteParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import type {OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {CoverPhoto} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import type {EventStyles} from '@Components/event-wizard/event-style/event-style.types';
import {redirectUser} from '@Utils/browser.util';
import type {EventWizardVenue} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {EventVenueType, isOfflineVenueComplete} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import type {EventVO, EventVOMinifiedWizardPrefill} from '@Components/events/events.types';
import {
  getDefaultEndDateTime,
  getDefaultRecurringEvent,
  getDefaultSelectedOrganiserDetails,
  getDefaultStartDateTime,
  getDefaultTimeZone,
  onEventWizardCoverPhotoUpdate,
  onEventWizardDescriptionChange,
  onEventWizardEndDateTimeUpdate,
  onEventWizardEventHIDChange,
  onEventWizardIsDesignPrefillContentLoadingUpdate,
  onEventWizardOfflineVenueChange,
  onEventWizardOnlineVenueChange,
  onEventWizardProgressStepLandedOnUpdate,
  onEventWizardProgressStepUpdate,
  onEventWizardRecurringChange,
  onEventWizardEventRegistrationChange,
  onEventWizardRegistrationModeChange,
  onEventWizardRemoveWatermarkToggle,
  onEventWizardSelectedOrganiserUpdate,
  onEventWizardSelectedTypeChange,
  onEventWizardShareAsGoogleEventToggle,
  onEventWizardShareInTeamToggle,
  onEventWizardTicketingConfigHydrate,
  onEventWizardStartDateTimeUpdate,
  onEventWizardStyleUpdate,
  onEventWizardTimeZoneUpdate,
  onEventWizardTitleChange,
  onEventWizardToggleCountdown,
} from '@Components/event-wizard/event-wizard-slice';
import type {TimeZone} from 'timezones-list';
import timezones from 'timezones-list';
import {convertTimeFromTimezoneToUTC, convertUTCTimeToGivenTimezone, getSQLFormatDate, getTimeDifferenceIntervals, hasDatedPassed} from '@Utils/date.util';
import {getDateTimeErrorType} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.helper';
import {DateTimeErrorType} from '@Components/event-wizard/event-wizard-date-time/event-wizard-date-time.types';
import {deriveSelectedRegistrationMode, getPromotePageURL, openUpsellDialogForEventsLimitReached} from '@Libraries/events.library';
import {getTicketingDashboardURL, isTicketedEvent, isTicketingAvailableForEvent} from '@Libraries/event-ticketing.library';
import isInteger from 'lodash/isInteger';
import isNaN from 'lodash/isNaN';
import {togglePreviewCoverPhotoLoading, togglePreviewTitleLoading, updatePreviewDateTime, updatePreviewTitle} from '@Components/event-wizard/preview/preview.helper';
import {isBasicSectionComplete} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {hideLoading, showLoading, smoothEstimateLoadingProgress, updateLoadingProgress} from '@Libraries/loading-toast-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {validateSocialMediaLinks} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {shouldUpsellAITemplateTagging} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {getGMTOffsetFromTimeZone} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {openUserEmailVerificationModal} from '@Modals/user-email-verification-modal/user-email-verification-modal.types';
import {isValidURL2} from '@Utils/url.util';
import {openUserEmailVerificationErrorModal} from '@Modals/user-email-verification-error-modal/user-email-verification-error-modal.types';
import {pollHasUserVerifiedEmail, sendUserVerificationEmail} from '@Libraries/user.library';

export const goToOriginPage = (): void => {
  window.history.back();
};

export const removeRedundantQueryParams = (): void => {
  history.replaceState({}, '', window.location.origin + window.location.pathname);
};

/**
 * Checks if an element has a class or is inside a parent class
 * @param element
 * @param classNameToExclude
 */
export const isElementInClass = (element: HTMLElement | null, classNameToExclude: string): boolean => {
  let elementRecur = element;

  while (elementRecur) {
    if (elementRecur.classList.contains(classNameToExclude)) {
      return true;
    }
    elementRecur = elementRecur.parentElement;
  }
  return false;
};

export const setReduxForDesignPrefill = (eventVOMinifiedWizardPrefill?: EventVOMinifiedWizardPrefill): void => {
  const designTemplateSource: MediaTypes | undefined = eventVOMinifiedWizardPrefill?.designTemplateSource;

  if (
    !eventVOMinifiedWizardPrefill?.designTemplateHID ||
    !eventVOMinifiedWizardPrefill.designTemplateURL ||
    designTemplateSource === undefined ||
    !(designTemplateSource in MediaTypes)
  ) {
    return;
  }

  const {dispatch} = window.PMW.redux.store;

  dispatch(onEventWizardIsDesignPrefillContentLoadingUpdate(true));

  dispatch(
    onEventWizardCoverPhotoUpdate({
      uploadedUrl: eventVOMinifiedWizardPrefill.designTemplateURL,
      selectedTemplateHID: eventVOMinifiedWizardPrefill.designTemplateHID,
      mediaType: eventVOMinifiedWizardPrefill.designTemplateSource,
    })
  );
  trackDesignPrefillGAEvent();
  prefillContentFromDesign(eventVOMinifiedWizardPrefill);
};

export const setReduxForStartDatePrefill = (eventVOMinifiedWizardPrefill?: EventVOMinifiedWizardPrefill): void => {
  if (!eventVOMinifiedWizardPrefill?.startDateTime) {
    return;
  }
  const {dispatch} = window.PMW.redux.store;
  const timeZone = getDefaultTimeZone();
  const startDateTime = new Date(eventVOMinifiedWizardPrefill.startDateTime);
  const endDateTime = addHoursToDate(startDateTime);
  const endDateTimeUTC = convertTimeFromTimezoneToUTC(endDateTime, getGMTOffsetFromTimeZone(timeZone));

  if (hasDatedPassed(endDateTimeUTC)) {
    return;
  }
  dispatch(onEventWizardStartDateTimeUpdate(startDateTime));
  dispatch(onEventWizardEndDateTimeUpdate(endDateTime));
};

export const setReduxForUpdatingEvent = (eventVO?: EventVO): void => {
  if (!eventVO) {
    return;
  }
  const {dispatch} = window.PMW.redux.store;

  dispatch(onEventWizardEventHIDChange(eventVO.hashedID));
  dispatch(onEventWizardTitleChange(eventVO.title));
  dispatch(onEventWizardSelectedTypeChange(eventVO.type));
  dispatch(onEventWizardDescriptionChange(eventVO.description));
  setReduxCoverPhotoFromUpdateEvent(eventVO);
  setReduxVenueFromUpdateEvent(eventVO);
  setReduxRecurringForUpdateEvent(eventVO.recurring);
  const timezone = setReduxTimezoneFromUpdateEvent(eventVO.tzcode);
  attemptToSetReduxDateTimeFromGivenStartAndEnd(new Date(eventVO.startDateTime), new Date(eventVO.endDateTime), timezone);
  dispatch(onEventWizardToggleCountdown(eventVO.showCountdown));
  dispatch(onEventWizardStyleUpdate(eventVO.style));
  setReduxOrganiserForUpdateEvent(eventVO);
  dispatch(onEventWizardShareInTeamToggle(eventVO.isShared));
  dispatch(onEventWizardRemoveWatermarkToggle(eventVO.isRemoveWatermark));
  dispatch(onEventWizardEventRegistrationChange(eventVO.registrationFields));
  dispatch(onEventWizardTicketingConfigHydrate(buildTicketingConfigFromEventVO(eventVO.ticketingConfig, eventVO.registrationFields.registerBtnText)));
  dispatch(
    onEventWizardRegistrationModeChange(
      deriveSelectedRegistrationMode(eventVO.ticketingConfig?.isSelected ?? false, eventVO.ticketingConfig?.isRsvp ?? false, eventVO.registrationFields.areRegistrationsRequired, eventVO.registrationFields.registerBtnLink)
    )
  );
  dispatch(onEventWizardShareAsGoogleEventToggle(!!eventVO.isSharedOnGoogle));
  dispatch(onEventWizardSelectedOrganiserUpdate(eventVO.organiser ?? getDefaultSelectedOrganiserDetails()));
};

export const doesContainKeywords = (searchText: string, keywords: string): boolean => {
  return searchText.toLowerCase().includes(keywords.toLowerCase());
};

export const addHoursToDate = (date: Date, hours = 1): Date => {
  return new Date(date.getTime() + hours * 60 * 60 * 1000);
};

export const saveEvent = async (data: EventWizardReduxState, onFailCB: (showErrModal: boolean) => void, pageSourceType: EventWizardSourceType): Promise<void> => {
  return window.PMW.writeLocal('event/saveEvent', prepareDataForEventCreation(data))
    .then((responseData: {eventVO: EventVO; isUserFirstEvent: boolean; wasTicketingPaidJustEnabled: boolean; needsStripeOnboarding: boolean}) => {
      onEventSuccessCreation(responseData.eventVO, pageSourceType, data, responseData.wasTicketingPaidJustEnabled, responseData.needsStripeOnboarding);
    })
    .catch((error: EventSaveErrorProps) => {
      onEventFailedCreation(error, onFailCB);
    });
};

export const getEventCreationTimeFormat = (dateTime: Date): string => {
  return dateTime.toLocaleString('en-us');
};

/**
 * Where the wizard drops the organiser after a save — the tab they will actually work in next, rather than the
 * public event page. Three destinations, in priority order.
 *
 * An organiser who still has to set up payments goes to the Tickets page, on CREATE and UPDATE alike, because
 * the onboarding view lives there and it is the same next job either way. **The backend owns the meaning of
 * that flag** — it is `isPaidTicketing() && requiresOnboardingTakeover()`, so a RESTRICTED organiser gets
 * `false` on purpose and lands on Promote where the banner explains the restriction. Do NOT branch on the
 * account state here: re-deriving the rule would need a second round trip purely to decide where to
 * navigate, and would re-open the two-copies-of-one-rule seam this closes.
 *
 * Otherwise, editing an existing ticketed event lands on its Tickets dashboard, where the orders are. A newly
 * created or duplicated event has no orders yet, so it lands on Promote whatever its registration mode is:
 * filling the seats is the organiser's next job. RSVP, custom-link and no-button events have nothing to
 * manage on the dashboard.
 *
 * `isTicketingAvailableForEvent` is not a re-derivation of the backend rule: it answers a different question
 * — may this VIEWER open the Tickets tab at all? Without it a viewer whose gating hides the tab would be
 * deep-linked to a page `event-ticketing.tsx`'s own redirect immediately bounces to #/my-events, which reads
 * as a broken save.
 */
const getPostSaveRedirectURL = (event: EventVO, pageSourceType: EventWizardSourceType, needsStripeOnboarding: boolean): string => {
  if (needsStripeOnboarding && isTicketingAvailableForEvent(event)) {
    return getTicketingDashboardURL(event);
  }
  if (pageSourceType === EventWizardSourceType.UPDATE && isTicketedEvent(event) && isTicketingAvailableForEvent(event)) {
    return getTicketingDashboardURL(event);
  }
  return getPromotePageURL(event);
};

const onEventSuccessCreation = (
  event: EventVO,
  pageSourceType: EventWizardSourceType,
  data: EventWizardReduxState,
  wasTicketingPaidJustEnabled: boolean,
  needsStripeOnboarding: boolean
): void => {
  try {
    handleEventSuccessCreationGAEvent(pageSourceType, data, wasTicketingPaidJustEnabled);
  } catch (e) {
    console.error(e);
  }
  redirectUser(getPostSaveRedirectURL(event, pageSourceType, needsStripeOnboarding), false, false);
};

const handleEventSuccessCreationGAEvent = (pageSourceType: EventWizardSourceType, data: EventWizardReduxState, wasTicketingPaidJustEnabled: boolean): void => {
  if (pageSourceType === EventWizardSourceType.UPDATE) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPDATE_CLICK);
    if (wasTicketingPaidJustEnabled) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_TICKETING_PAID_ENABLED);
    }
    return;
  }
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_COMPLETE, {
    [GA4EventParamName.EVENT_TYPE]: getEventTypeGa4Param(data.selectedRegistrationMode, data.ticketingConfig),
  });
  if (data.selectedRegistrationMode === RegistrationMode.TICKETS) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_TICKETING_SETUP_COMPLETE, getTicketingSetupCompleteParams(data.ticketingConfig));
  }
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_RECURRING, {[GA4EventParamName.TYPE]: data.recurringEvent.isRecurring ? 'Recurring' : 'Non-recurring'});
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VENUE, {
    [GA4EventParamName.TYPE]: data.venue.selectedVenueType === EventVenueType.OFFLINE ? 'Offline' : 'Online',
  });

  if (data.coverPhoto.mediaType === MediaTypes.UPLOAD) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'Upload'});
  } else if (data.coverPhoto.mediaType === MediaTypes.MY_DESIGNS) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'My Designs'});
  } else if (data.coverPhoto.mediaType === MediaTypes.TEMPLATES) {
    if (shouldUpsellAITemplateTagging(data.doesPAYGUserHaveAITemplateLimit)) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'Gallery non AI'});
    } else {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_VISUALS, {[GA4EventParamName.TYPE]: 'AI'});
    }
  }

  if (data.isSharedInTeam) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_SHARE_TEAM);
  }
  const validatedSocialMediaLinks = validateSocialMediaLinks(data.organisers.selectedOrganiser.socialMediaLinks);

  if (validatedSocialMediaLinks.isValid) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PAGE_SOCIALS);
  }
};

const onEventFailedCreation = (error: EventSaveErrorProps, onFailCB: (showErrModal: boolean) => void): void => {
  const errMsg = error.message;

  if (errMsg === EventCreationErrorType.NUM_CREATION_LIMIT.toString()) {
    openUpsellDialogForEventsLimitReached();
    onFailCB(false);
    return;
  }

  if (errMsg === EventCreationErrorType.RACE_CONDITION.toString()) {
    onFailCB(true);
    return;
  }

  if (errMsg === EventCreationErrorType.RECURRING_PAID_TICKETING_NOT_SUPPORTED.toString()) {
    showMessageGrowl({
      key: MESSAGE_GROWL_KEY,
      text: window.i18next.t('pmwjs_recurring_paid_ticketing_save_failed'),
      interval: 15000,
      type: GROWL_TYPE.DANGER,
      showCloseIcon: true,
    });
    onFailCB(false);
    return;
  }

  if (errMsg === EventCreationErrorType.USER_EMAIL_VERIFICATION_REQUIRED.toString()) {
    if (error.data.errorMessage) {
      onFailCB(false);
      openUserEmailVerificationErrorModal({errorMsg: error.data.errorMessage});
      return;
    }
    openUserEmailVerificationModal({
      userEmail: error.data.userEmail,
      sniperLink: error.data.sniperLink,
      onClose: () => {
        onFailCB(false);
      },
      onVerifiedCB: () => {
        redirectUser(window.PMW.util.site_url('event/redirectToLatestOrganiserEventAfterEmailVerification'), false, false);
      },
      pollVerification: pollHasUserVerifiedEmail,
      sendVerification: () => sendUserVerificationEmail({is_event: 1}),
    });
    return;
  }
  onFailCB(true);
};

const setReduxCoverPhotoFromUpdateEvent = (eventVO: EventVO): void => {
  let url = eventVO.img,
    mediaType = undefined,
    uploadMediaHID = undefined;

  if (eventVO.coverPhotoTemplateHID && eventVO.coverPhotoPreviewURL) {
    mediaType = MediaTypes.MY_DESIGNS;
    url = eventVO.coverPhotoPreviewURL;
  } else if (url) {
    mediaType = MediaTypes.UPLOAD;
    const filenameWithExt = url.substring(url.lastIndexOf('/') + 1);
    uploadMediaHID = filenameWithExt.substring(0, filenameWithExt.lastIndexOf('.'));
  }
  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({mediaType: mediaType, uploadedUrl: url, selectedTemplateHID: eventVO.coverPhotoTemplateHID, uploadMediaHID: uploadMediaHID})
  );
};

const setReduxVenueFromUpdateEvent = (eventVO: EventVO): void => {
  const {dispatch} = window.PMW.redux.store;

  if (eventVO.venueOnline) {
    dispatch(onEventWizardOnlineVenueChange(eventVO.venueOnline.link));
    return;
  }

  if (eventVO.venue && isOfflineVenueComplete(eventVO.venue)) {
    dispatch(onEventWizardOfflineVenueChange(eventVO.venue));
  }
};

const setReduxTimezoneFromUpdateEvent = (tzcode: string): TimeZone => {
  for (const timezone of timezones) {
    if (timezone.tzCode === tzcode) {
      window.PMW.redux.store.dispatch(onEventWizardTimeZoneUpdate(timezone));
      return timezone;
    }
  }
  return getDefaultTimeZone();
};

const setReduxRecurringForUpdateEvent = (recurring: RecurringEvent | undefined): void => {
  if (!recurring?.recurringEndDateStr) {
    return;
  }
  recurring.recurringEndDate = new Date(recurring.recurringEndDateStr);

  const defaultRecurring = getDefaultRecurringEvent();

  if (recurring.recurringType === RecurringType.WEEKLY) {
    recurring.recurringMonthly = defaultRecurring.recurringMonthly;
  } else if (recurring.recurringType === RecurringType.MONTHLY) {
    recurring.recurringWeeks = defaultRecurring.recurringWeeks;
  } else {
    recurring.recurringWeeks = defaultRecurring.recurringWeeks;
    recurring.recurringMonthly = defaultRecurring.recurringMonthly;
  }
  window.PMW.redux.store.dispatch(onEventWizardRecurringChange({...recurring, isRecurring: true}));
};

const attemptToSetReduxDateTimeFromGivenStartAndEnd = (startDateTimeUTC: Date, endDateTimeUTC: Date, timeZone: TimeZone): Date[] => {
  const {dispatch} = window.PMW.redux.store;

  const errType = getDateTimeErrorType(startDateTimeUTC, endDateTimeUTC, timeZone, true);
  let startDateTimeUpdated: Date = startDateTimeUTC;
  let endDateTimeUpdated: Date = endDateTimeUTC;

  if (errType && errType !== DateTimeErrorType.EVENT_PASSED_DATE && errType !== DateTimeErrorType.EVENT_PASSED_TIME) {
    return [];
  }

  if (errType) {
    // If the event has passed, it goes to the next day from the current day WHILE keeping the hour/minute data the same
    const {intervalInDays} = getTimeDifferenceIntervals((endDateTimeUTC.getTime() - startDateTimeUTC.getTime()) / 1000);
    const currentTimeInUTC = convertTimeFromTimezoneToUTC(new Date(), getGMTOffsetFromTimeZone(timeZone));
    startDateTimeUpdated = new Date(startDateTimeUTC.getTime());
    startDateTimeUpdated.setDate(currentTimeInUTC.getDate() + 1);
    endDateTimeUpdated = new Date(endDateTimeUTC.getTime());
    endDateTimeUpdated.setFullYear(startDateTimeUpdated.getFullYear(), startDateTimeUpdated.getMonth(), startDateTimeUpdated.getDate() + intervalInDays);
  }

  const userInputStartDateTime = convertUTCTimeToGivenTimezone(startDateTimeUpdated, getGMTOffsetFromTimeZone(timeZone));
  const userInputEndDateTime = convertUTCTimeToGivenTimezone(endDateTimeUpdated, getGMTOffsetFromTimeZone(timeZone));
  dispatch(onEventWizardStartDateTimeUpdate(userInputStartDateTime));
  dispatch(onEventWizardEndDateTimeUpdate(userInputEndDateTime));

  return [userInputStartDateTime, userInputEndDateTime];
};

const setReduxOrganiserForUpdateEvent = (eventVO: EventVO): void => {
  if (!eventVO.organiser) {
    return;
  }
  const {dispatch} = window.PMW.redux.store;
  dispatch(onEventWizardSelectedOrganiserUpdate({...eventVO.organiser, isProfilePicturePrefilled: !!eventVO.organiser.profilePic}));
};

const prepareDataForEventCreation = (data: EventWizardReduxState) => {
  const venueData = getVenueData(data.venue);
  const registrationFields = getRegistrationFieldsData(
    data.registrationDetails,
    getEffectiveRegisterBtnText(data.selectedRegistrationMode, data.ticketingConfig, data.registrationDetails)
  );
  const organiserData = getOrganisationData(data.organisers.selectedOrganiser);
  const coverPhotoData = getCoverPhotoData(data.coverPhoto);
  const styles = getStyleData(data.styles);

  return {
    event_hid: data.eventHID,
    title: data.title,
    event_type_hid: data.types?.selectedType?.hashedID,
    description: data.description,
    ...coverPhotoData,
    ...venueData,
    date_start: getEventCreationTimeFormat(data.startDateTime),
    date_end: getEventCreationTimeFormat(data.endDateTime),
    recurring_pattern: getRecurringPatternForPost(data.recurringEvent),
    timezone: data.timeZone.tzCode,
    show_countdown: data.showCountdown ? 1 : 0,
    ...styles,
    ...organiserData,
    is_shared_team: data.isSharedInTeam ? 1 : 0,
    is_remove_watermark: data.isRemoveWatermark ? 1 : 0,
    is_shared_google: data.isSharedAsGoogleEvent ? 1 : 0,
    ...registrationFields,
    ...getTicketingConfigData(data.ticketingConfig),
    is_duplicate: data.pageSourceType === EventWizardSourceType.DUPLICATE ? 1 : 0,
    is_description_user_made: data.isDescriptionUserMade ? 1 : 0,
  };
};

const getVenueData = (venue: EventWizardVenue) => {
  if (venue.selectedVenueType === EventVenueType.OFFLINE) {
    return getOfflineVenueData(venue.offlineVenue);
  }

  if (venue.selectedVenueType === EventVenueType.ONLINE) {
    return getOnlineVenueData(venue.onlineVenueURL);
  }
  return null;
};

const getOnlineVenueData = (url: string | undefined) => {
  if (!url) {
    return null;
  }

  return {
    online_event_link: url,
  };
};

const getOfflineVenueData = (place: PlaceDetails | undefined) => {
  if (!place) {
    return null;
  }

  return {
    offline_venue: {
      venue_id: place.idAddress,
      venue_new_place_id: place.placeID,
      name: place.name,
      address: place.address,
      address_directions: place.addressDirections,
      lng: place.longitude,
      lat: place.latitude,
      country: place.country,
      province: place.province,
      city: place.city,
      postal_code: place.postalCode,
    },
  };
};

const getRegistrationFieldsData = (registrationFields: EventRegistrationFields, registerBtnText: string) => {
  return {
    registration_fields: {
      are_registrations_required: registrationFields.areRegistrationsRequired ? 1 : 0,
      name: registrationFields.name ? 1 : 0,
      phone: registrationFields.phone ? 1 : 0,
      register_btn_text: registerBtnText,
      register_btn_link: isValidURL2(registrationFields.registerBtnLink ?? '') ? registrationFields.registerBtnLink : undefined,
    },
  };
};

const getTicketingConfigData = (config: TicketingConfig): Record<string, unknown> => {
  if (!config.isSelected) {
    return {}; // omit ticketing_config entirely — backend validates unconditionally otherwise
  }
  return {
    ticketing_config: {
      is_selected: 1,
      is_rsvp: config.isRsvp ? 1 : 0,
      ticket_name: config.ticketName,
      price: config.price,
      total_tickets: config.totalTickets,
      max_per_order: config.maxPerOrder,
      pass_fees_to_buyer: config.passFeesToBuyer && config.price > 0 ? 1 : 0,
      tax_label: config.taxLabel,
      tax_rate: config.taxRatePercent,
      tax_mode: config.taxMode,
      who_responds: config.whoResponds,
      question_section_title: getEffectiveQuestionSectionTitle(config),
      question_section_description: config.questionSectionDescription,
      questions: config.questions.map((q, index) => ({
        text: q.text,
        type: q.type,
        required: q.required ? 1 : 0,
        order: index,
        options: q.options,
      })),
    },
  };
};

const getOrganisationData = (organiserDetails: OrganiserDetails) => {
  return {
    organiser_details: {
      hid: organiserDetails.hashedId,
      name: organiserDetails.name,
      email: organiserDetails.email,
      phone: organiserDetails.phone,
      pic: organiserDetails.profilePic,
      isPicPrefilled: organiserDetails.isProfilePicturePrefilled ? 1 : 0,
      socialMediaLinks: organiserDetails.socialMediaLinks,
    },
  };
};

const getRecurringPatternForPost = (recurringEvent: RecurringEvent) => {
  if (!recurringEvent.isRecurring) {
    return null;
  }
  return {
    recurring_type: recurringEvent.recurringType.toString(),
    recurring_end_date: getEventCreationTimeFormat(recurringEvent.recurringEndDate),
    recurring_weeks: recurringEvent.recurringWeeks ? recurringEvent.recurringWeeks.join(',') : null,
    recurring_month_weekday: recurringEvent.recurringMonthly?.weekday,
    recurring_month_weekday_num: recurringEvent.recurringMonthly?.weekdayNum,
  };
};

const getCoverPhotoData = (coverPhoto: CoverPhoto) => {
  if (coverPhoto.selectedTemplateHID) {
    return {
      poster_hid: coverPhoto.selectedTemplateHID,
    };
  }
  return {
    uploaded_media_hid: coverPhoto.uploadMediaHID,
  };
};

const getStyleData = (eventStyles: EventStyles) => {
  return {
    style: {
      colors: {
        primary: eventStyles.colors.primary,
        light: eventStyles.colors.light,
        lighter: eventStyles.colors.lighter,
        dark: eventStyles.colors.dark,
        darker: eventStyles.colors.darker,
        background: eventStyles.colors.background ?? null,
        buttonTextColor: eventStyles.colors.buttonTextColor ?? null,
      },
      font: eventStyles.fontFamily,
    },
  };
};

const prefillContentFromDesign = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): void => {
  togglePreviewCoverPhotoLoading(true);
  togglePreviewTitleLoading(true);

  showLoading(LOADING_TOAST_KEY, {
    key: LOADING_TOAST_KEY,
    priority: 1,
    text: window.i18next.t('pmwjs_updating_details_with_ai'),
    hideIcon: true,
    progress: 0,
  });
  const estimateLoadingTimeMs = 5000;
  smoothEstimateLoadingProgress(LOADING_TOAST_KEY, estimateLoadingTimeMs, 500);

  const {dispatch} = window.PMW.redux.store;

  void window.PMW.writeLocal('event/getDesignReverseSemanticTaggingContent', {
    poster_hid: eventVOMinifiedWizardPrefill.designTemplateHID,
  })
    .then(setDesignContentFromReverseSemanticTagging)
    .catch((e: Error) => {
      hideLoading(LOADING_TOAST_KEY);

      if (e.message === 'limit_reached' || e.message === 'limit_reached_payg') {
        showMessageGrowl({
          key: MESSAGE_GROWL_KEY,
          text: e.message === 'limit_reached' ? window.i18next.t('pmwjs_daily_ai_generation_limit_reached') : window.i18next.t('pmwjs_ai_generation_limit_reached_payg'),
          interval: 15000,
          type: GROWL_TYPE.DANGER,
          showCloseIcon: true,
        });
      }
    })
    .finally(() => {
      dispatch(onEventWizardIsDesignPrefillContentLoadingUpdate(false));
      togglePreviewCoverPhotoLoading(false);
      togglePreviewTitleLoading(false);
    });
};

const setDesignContentFromReverseSemanticTagging = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): void => {
  const {dispatch} = window.PMW.redux.store;

  if (eventVOMinifiedWizardPrefill.eventName) {
    dispatch(onEventWizardTitleChange(eventVOMinifiedWizardPrefill.eventName));
    updatePreviewTitle(eventVOMinifiedWizardPrefill.eventName);
  }

  if (eventVOMinifiedWizardPrefill.eventType) {
    dispatch(onEventWizardSelectedTypeChange(eventVOMinifiedWizardPrefill.eventType));
  }

  try {
    const startAndEndDateTime = updateDateTimeFromDesignPrefill(eventVOMinifiedWizardPrefill);

    if (startAndEndDateTime.length === 2) {
      eventVOMinifiedWizardPrefill.startDateTime = getSQLFormatDate(startAndEndDateTime[0]);
      eventVOMinifiedWizardPrefill.endDateTime = getSQLFormatDate(startAndEndDateTime[1]);
    }
  } catch (error: unknown) {
    const errMsg = error instanceof Error ? error.message : '';
    window.PMW.log(errMsg + '. ' + EVENT_ERROR_LOG_TAG);
  }

  // selfCodeReviewJibran: Location needs handling. Hit gmap API and set. Attempt timezone too: // location?: string;

  if (isBasicSectionCompleteFromMinifiedVO(eventVOMinifiedWizardPrefill)) {
    dispatch(onEventWizardProgressStepUpdate(EventWizardProgressStep.REVIEW));
    dispatch(onEventWizardProgressStepLandedOnUpdate(EventWizardProgressStep.REVIEW));
  }
  updateLoadingProgress(LOADING_TOAST_KEY, 1.0);
  hideLoading(LOADING_TOAST_KEY);
};

const isBasicSectionCompleteFromMinifiedVO = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): boolean => {
  if (
    !eventVOMinifiedWizardPrefill.eventName ||
    !eventVOMinifiedWizardPrefill.eventType ||
    !eventVOMinifiedWizardPrefill.startDateTime ||
    !eventVOMinifiedWizardPrefill.endDateTime
  ) {
    return false;
  }

  return isBasicSectionComplete(
    eventVOMinifiedWizardPrefill.eventName,
    {}, // selfCodeReviewJibran: when gmap api ready. add in if statement above too
    new Date(eventVOMinifiedWizardPrefill.startDateTime),
    new Date(eventVOMinifiedWizardPrefill.endDateTime),
    getDefaultTimeZone()
  );
};

const updateDateTimeFromDesignPrefill = (eventVOMinifiedWizardPrefill: EventVOMinifiedWizardPrefill): Date[] => {
  if (eventVOMinifiedWizardPrefill.startDate && !eventVOMinifiedWizardPrefill.endDate) {
    eventVOMinifiedWizardPrefill.endDate = eventVOMinifiedWizardPrefill.startDate;
  }

  const startDateTime = getUpdateDateTimeFromDesignPrefill(getDefaultStartDateTime(), eventVOMinifiedWizardPrefill.startDate, eventVOMinifiedWizardPrefill.startTime);
  let endDateTime = getUpdateDateTimeFromDesignPrefill(getDefaultEndDateTime(), eventVOMinifiedWizardPrefill.endDate, eventVOMinifiedWizardPrefill.endTime);
  const timeZone = getDefaultTimeZone();

  if (eventVOMinifiedWizardPrefill.startTime && !eventVOMinifiedWizardPrefill.endTime) {
    endDateTime = addHoursToDate(startDateTime);
  }
  const startAndEndDateTime = attemptToSetReduxDateTimeFromGivenStartAndEnd(
    convertTimeFromTimezoneToUTC(startDateTime, getGMTOffsetFromTimeZone(timeZone)),
    convertTimeFromTimezoneToUTC(endDateTime, getGMTOffsetFromTimeZone(timeZone)),
    timeZone
  );

  if (startAndEndDateTime.length === 2) {
    updatePreviewDateTime({
      startDateTime: startAndEndDateTime[0],
      endDateTime: startAndEndDateTime[1],
      timeZone: timeZone,
    });
  }
  return startAndEndDateTime;
};

const getUpdateDateTimeFromDesignPrefill = (defaultDateTime: Date, updatedDate: string | undefined, updatedTime: string | undefined): Date => {
  if (!updatedTime && !updatedDate) {
    return defaultDateTime;
  }

  if (updatedDate) {
    const newStartDate = new Date(updatedDate);

    if (!isNaN(newStartDate.getTime())) {
      defaultDateTime.setFullYear(defaultDateTime.getFullYear(), newStartDate.getMonth(), newStartDate.getDate());
    }
  }

  if (updatedTime) {
    const [hours, minutes, _] = updatedTime.split(':').map(Number);

    if (isInteger(hours) && isInteger(minutes)) {
      defaultDateTime.setHours(hours, minutes, 0, 0);
    }
  }
  return defaultDateTime;
};

const trackDesignPrefillGAEvent = (): void => {
  const referrer = document.referrer;
  let url = null;

  try {
    url = new URL(referrer);
  } catch (e) {
    return;
  }

  if (url === null) {
    return;
  }
  const segments = url.pathname
    .split('/')
    .filter(Boolean)
    .filter((segment) => segment !== 'pmw' && segment !== 'index.php');
  const firstSegment = segments[0];
  const firstTwoSegments = segments.slice(0, 2).join('/');

  let createClickSrc = '';

  if (firstTwoSegments === 'art/template') {
    createClickSrc = 'Art/template page';
  } else if (firstTwoSegments === 'poster/view') {
    createClickSrc = 'Poster/view page';
  } else if (firstSegment === 'posterbuilder') {
    createClickSrc = 'Design editor';
  }

  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK, createClickSrc ? {[GA4EventParamName.SOURCE]: createClickSrc} : undefined);
};
